<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useOperatorApi } from '../composables/useOperatorApi'
import type { DailyOutputRow } from '../types/operator'

const { t } = useI18n()
const { fetchDailyOutput, summarizeRows } = useOperatorApi()

const reportDate = ref(new Date().toLocaleDateString('en-CA'))
const rows = ref<DailyOutputRow[]>([])
const loading = ref(true)
const error = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const output = await fetchDailyOutput(reportDate.value)
    rows.value = output.rows
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch(reportDate, load)
onMounted(load)

const totals = computed(() => summarizeRows(rows.value))

const stageRows = computed(() => {
  const map = new Map<string, DailyOutputRow>()
  for (const row of rows.value) {
    const key = row.stage_code
    const existing = map.get(key)
    if (existing) {
      existing.completed_quantity = Number(existing.completed_quantity || 0) + Number(row.completed_quantity || 0)
      existing.rejected_quantity = Number(existing.rejected_quantity || 0) + Number(row.rejected_quantity || 0)
      existing.rework_quantity = Number(existing.rework_quantity || 0) + Number(row.rework_quantity || 0)
    } else {
      map.set(key, { ...row })
    }
  }
  return Array.from(map.values())
})

function efficiencyPercent(row: DailyOutputRow): number {
  const good = Number(row.completed_quantity || 0)
  const bad = Number(row.rejected_quantity || 0)
  const total = good + bad
  if (total <= 0) return 100
  return Math.round((good / total) * 100)
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('operator.efficiency.eyebrow') }}</p>
      <h2>{{ t('operator.efficiency.title') }}</h2>
      <p>{{ t('operator.efficiency.description') }}</p>
    </div>
    <label class="date-field">
      <span>{{ t('operator.efficiency.date') }}</span>
      <input v-model="reportDate" type="date" class="date-input" />
    </label>
  </div>

  <!-- Totals -->
  <div v-if="!loading && !error && rows.length" class="summary-row">
    <div class="summary-card">
      <span class="summary-value good">{{ totals.completed.toLocaleString() }}</span>
      <span class="summary-label">{{ t('operator.efficiency.totalCompleted') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value warn">{{ totals.rejected.toLocaleString() }}</span>
      <span class="summary-label">{{ t('operator.efficiency.totalRejected') }}</span>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('operator.efficiency.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('operator.efficiency.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="stageRows.length === 0" class="state-message">
    <p>{{ t('operator.efficiency.noData') }}</p>
  </div>

  <!-- Table -->
  <table v-else class="data-table">
    <thead>
      <tr>
        <th>{{ t('operator.efficiency.stage') }}</th>
        <th>{{ t('operator.efficiency.completed') }}</th>
        <th>{{ t('operator.efficiency.rejected') }}</th>
        <th>{{ t('operator.efficiency.rework') }}</th>
        <th>{{ t('operator.efficiency.efficiency') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in stageRows" :key="row.stage_code">
        <td>{{ row.stage_name }}</td>
        <td class="cell-qty good">{{ Number(row.completed_quantity || 0).toLocaleString() }}</td>
        <td class="cell-qty warn">{{ Number(row.rejected_quantity || 0).toLocaleString() }}</td>
        <td class="cell-qty warn">{{ Number(row.rework_quantity || 0).toLocaleString() }}</td>
        <td>
          <div class="efficiency">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: efficiencyPercent(row) + '%' }" />
            </div>
            <span>{{ t('operator.efficiency.efficiencyValue', { percent: efficiencyPercent(row) }) }}</span>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
}
.date-input {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
}

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }

.summary-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 28px;
  text-align: center;
}
.summary-value { display: block; font-size: 26px; font-weight: 800; color: #1a2b26; }
.summary-value.good { color: #1f5c4b; }
.summary-value.warn { color: #b3472d; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-qty { font-weight: 700; }
.good { color: #1f5c4b; }
.warn { color: #b3472d; }

.efficiency { display: flex; align-items: center; gap: 10px; }
.progress-bar {
  width: 120px;
  height: 6px;
  background: #e2e6e3;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: #147a8a;
  border-radius: 3px;
  transition: width 0.3s;
}
.efficiency span { font-size: 12px; font-weight: 700; color: #147a8a; }

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:hover { background: #0f6573; }
</style>
